import "./ResponseUsageMeter.scss";

import { Alert, Button, Flex, InfoIcon, Text, Tooltip } from "@fluentui/react-northstar";
import { dialog, DialogDimension } from "@microsoft/teams-js";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router";

import { useUserInfo } from "../../actions/useUserInfo";
import CoreLogo from "../../assets/polly-logo-no-text.svg";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { getCheckOutTaskInfo, startCheckoutTask } from "../../utils/startTaskWrapper";
import useWindowDimensions from "../../utils/useWindowDimensions";
import { PollyDialog } from "../PollyDialog";

export const ResponseUsageMeter: React.FC = () => {
  const { user } = useUserInfo();
  const { teamsContext } = useTeamsContext();
  const { loadUser } = useUserInfo();
  const history = useHistory();
  const { width } = useWindowDimensions();

  const [showCheckoutAlert, setShowCheckoutAlert] = useState<boolean>(false);

  const percentage = useMemo(() => {
    if (!user.featureFlags.responseUsageLimit) {
      return 0;
    }

    const perc = user.usage / user.featureFlags.responseUsageLimit;
    const value = Math.round(perc * 100);
    if (value > 100) {
      return 100;
    }

    return value;
  }, [user]);

  if (user.featureFlags.responseUsageLimit >= 100000) {
    return null;
  }

  return (
    <>
      {showCheckoutAlert && (
        <PollyDialog
          open
          onCancel={() => {
            setShowCheckoutAlert(false);
          }}
          maxWidth="410px"
          className="alert-dialog"
          header={
            <Flex className="alert-dialog-header">
              <img src={CoreLogo} height={40} alt="Polly" />
              <Flex column className="alert-dialog-header__heading">
                <Text weight="bold" size="medium">
                  Polly
                </Text>
                <Text weight="regular" size="medium">
                  Plans
                </Text>
              </Flex>
            </Flex>
          }
        >
          <Alert
            className="alert-dialog-alert"
            warning
            content={
              <>
                <Flex gap="gap.small" hAlign="start" vAlign="start">
                  <InfoIcon className="alert-dialog-info-icon" size="medium" outline />
                  <Text>
                    You will now be sent to the checkout page. Any unsaved changes will be lost.
                    <br />
                    Do you want to proceed?
                  </Text>
                </Flex>
              </>
            }
          />
          <Flex gap="gap.small" hAlign="end">
            <Button
              secondary
              content="Cancel"
              onClick={() => {
                setShowCheckoutAlert(false);
              }}
            />
            <Button
              primary
              content="Proceed to Plans"
              onClick={() => {
                const checkOutTaskInfo = getCheckOutTaskInfo(
                  user.subscriptions.length === 0,
                  "response-usage-meter",
                  width
                );
                dialog.update.resize({ height: checkOutTaskInfo.height || DialogDimension.Large, width: checkOutTaskInfo.width || DialogDimension.Large });
                history.push(checkOutTaskInfo.url!);
              }}
            />
          </Flex>
        </PollyDialog>
      )}
      <Tooltip content={`You have used ${user.usage} responses so far this month`}>
        <Flex
          column
          gap="gap.smaller"
          className="response-usage-meter"
          onClick={() => {
            if (!user.subscriptions.some(sub => sub.state === "active" && sub.type === "full")) {
              if (teamsContext?.page.frameContext === "task") {
                setShowCheckoutAlert(true);
              } else {
                startCheckoutTask(
                  user.subscriptions.length === 0,
                  "response-usage-meter",
                  width,
                  () => {
                    loadUser(true);
                  }
                );
              }
            }
          }}
        >
          <Flex gap="gap.smaller" vAlign="center">
            <Text
              weight="bold"
              size="small"
              content={`${user.usage}/${user.featureFlags.responseUsageLimit}`}
            />
            <Text content="Monthly responses" size="small" />
            <InfoIcon
              outline
              size="small"
              onClick={e => {
                window.open(
                  "https://www.polly.ai/help/microsoft-teams/response-limitations-explained",
                  "_blank"
                );
                e.preventDefault();
              }}
            />
          </Flex>

          <Flex hAlign="stretch">
            <div className="meter">
              <div
                className={`bar ${
                  percentage >= user.featureFlags.responseUsageWarningPercentage
                    ? "bar-warning"
                    : ""
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </Flex>
        </Flex>
      </Tooltip>
    </>
  );
};
